import graphql from 'babel-plugin-relay/macro'
import React, { Suspense, useContext, useEffect } from 'react'
import {
  PreloadedQuery,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay/hooks'
import { Route, Routes } from 'react-router-dom'

import AppManagementRouterRedirect from '../../AppManagementRouterRedirect'
import AppSettingsContext from '../../context'
import { AppAccessRouterQuery } from './__generated__/AppAccessRouterQuery.graphql'

const CreateApiKeyScene = React.lazy(() => import('./apiKey/CreateApiKeyScene'))
const AppApiKeysScene = React.lazy(() => import('./AppApiKeysScene'))
const AppLicenseTokensScene = React.lazy(
  () => import('./AppLicenseTokensScene'),
)
const AppAuthenticationScene = React.lazy(
  () => import('./authentication/AppAuthenticationScene'),
)

export const query = graphql`
  query AppAccessRouterQuery($appId: ID!) {
    viewer {
      appById(id: $appId) {
        viewerCanCreateApiKey
      }
    }
  }
`

type ContainerProps = {
  appId: string
}

export default function AppAccessRouterContainer({ appId }: ContainerProps) {
  const [queryRef, loadQuery] = useQueryLoader<AppAccessRouterQuery>(query)

  useEffect(() => {
    loadQuery(
      { appId },
      {
        fetchPolicy: 'store-and-network',
      },
    )
  }, [loadQuery, appId])

  if (!queryRef) {
    return null
  }

  return (
    <Suspense fallback={null}>
      <AppAccessRouter queryRef={queryRef} appId={appId} />
    </Suspense>
  )
}

type Props = {
  queryRef: PreloadedQuery<AppAccessRouterQuery>
} & ContainerProps

/** Router for the Access pages (authentication, API keys and offline license tokens). */
export function AppAccessRouter({ appId, queryRef }: Props) {
  const { viewer } = usePreloadedQuery<AppAccessRouterQuery>(query, queryRef)
  // Data gates come pre-computed (staff override baked in) from context so the
  // router always agrees with the nav.
  const {
    viewerCanReadApiKey,
    allowLicenseTokens = false,
    defaultArea = 'settings',
    allowAuthentication = false,
  } = useContext(AppSettingsContext)

  const canCreateApiKey = viewer.appById?.viewerCanCreateApiKey || false

  return (
    <Routes>
      {allowAuthentication && (
        <Route path="authentication" element={<AppAuthenticationScene />} />
      )}
      {viewerCanReadApiKey && (
        <Route path="api-keys" element={<AppApiKeysScene appId={appId} />} />
      )}
      {canCreateApiKey && (
        <Route
          path="api-keys/new"
          element={<CreateApiKeyScene appId={appId} />}
        />
      )}
      {allowLicenseTokens && (
        <Route
          path="license-tokens"
          element={<AppLicenseTokensScene appId={appId} />}
        />
      )}
      <Route
        path="*"
        element={
          <AppManagementRouterRedirect
            area={
              allowAuthentication
                ? 'access/authentication'
                : viewerCanReadApiKey
                  ? 'access/api-keys'
                  : allowLicenseTokens
                    ? 'access/license-tokens'
                    : defaultArea
            }
          />
        }
      />
    </Routes>
  )
}
